import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";

import Layout from "./components/Layout";
import Home from "./pages/Home";
import ReviewForm from "./pages/ReviewForm";
import Results from "./pages/Results";
import Booking from "./pages/Booking";
import About from "./pages/About";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import AdminLogin from "./pages/AdminLogin";
import AdminDashboard from "./pages/AdminDashboard";
import AdminAgents from "./pages/AdminAgents";
import AdminBranding from "./pages/AdminBranding";
import AgentDashboard from "./pages/AgentDashboard";
import AgentProfile from "./pages/AgentProfile";
import AgentPublicPage from "./pages/AgentPublicPage";
import Pitch from "./pages/Pitch";
import DemoForFMOs from "./pages/DemoForFMOs";
import DemoBadge from "./components/DemoBadge";
import PrepareReview from "./pages/PrepareReview";
import WealthLayout from "./components/WealthLayout";
import WealthHome from "./pages/wealth/WealthHome";
import Assessment from "./pages/wealth/Assessment";
import FreeAssessment from "./pages/wealth/FreeAssessment";
import WealthResults from "./pages/wealth/WealthResults";
import ToolkitLibrary from "./pages/wealth/ToolkitLibrary";
import { getUserRole } from "./lib/auth";
import { BrandingProvider } from "./lib/branding";
import { isWealthSite } from "./lib/site";

function ProtectedRoute({ children, role }) {
    const userRole = getUserRole();
    if (!userRole) return <Navigate to="/admin/login" replace />;
    if (role && userRole !== role) {
        return <Navigate to={userRole === "admin" ? "/admin" : "/agent"} replace />;
    }
    return children;
}

function ScrollToTop() {
    const { pathname } = useLocation();
    useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    }, [pathname]);
    return null;
}

function HostnameRoot() {
    // When the user lands on the GetYourWealthReport hostname directly, push
    // them into the wealth funnel. In preview, query `?site=wealth` also triggers this.
    if (isWealthSite()) return <Navigate to="/wealth" replace />;
    return <Layout><Home /></Layout>;
}

function App() {
    return (
        <div className="App">
            <BrandingProvider>
                <Toaster position="top-right" richColors />
                <BrowserRouter>
                    <ScrollToTop />
                    <DemoBadge />
                    <Routes>
                        <Route path="/" element={<HostnameRoot />} />
                        <Route path="/review" element={<Layout><ReviewForm /></Layout>} />
                        <Route path="/review-demo" element={<Layout><ReviewForm demoMode={true} /></Layout>} />
                        <Route path="/results" element={<Layout><Results /></Layout>} />
                        <Route path="/booking" element={<Layout><Booking /></Layout>} />
                        <Route path="/prepare-review" element={<Layout><PrepareReview /></Layout>} />
                        <Route path="/about" element={<Layout><About /></Layout>} />
                        <Route path="/privacy" element={<Layout><Privacy /></Layout>} />
                        <Route path="/terms" element={<Layout><Terms /></Layout>} />

                        {/* Wealth Report site (GetYourWealthReport.website) */}
                        <Route path="/wealth" element={<WealthLayout><WealthHome /></WealthLayout>} />
                        <Route path="/wealth/assessment" element={<WealthLayout><FreeAssessment /></WealthLayout>} />
                        <Route path="/wealth/assessment-paid" element={<WealthLayout><Assessment /></WealthLayout>} />
                        <Route path="/wealth/results" element={<WealthLayout><WealthResults /></WealthLayout>} />
                        <Route path="/wealth/library" element={<WealthLayout><ToolkitLibrary /></WealthLayout>} />
                        <Route path="/wealth/toolkit-library" element={<WealthLayout><ToolkitLibrary /></WealthLayout>} />

                        <Route path="/admin/login" element={<AdminLogin />} />
                        <Route path="/admin" element={<ProtectedRoute role="admin"><AdminDashboard /></ProtectedRoute>} />
                        <Route path="/admin/agents" element={<ProtectedRoute role="admin"><AdminAgents /></ProtectedRoute>} />
                        <Route path="/admin/branding" element={<ProtectedRoute role="admin"><AdminBranding /></ProtectedRoute>} />
                        <Route path="/agent" element={<ProtectedRoute role="agent"><AgentDashboard /></ProtectedRoute>} />
                        <Route path="/agent/me/profile" element={<ProtectedRoute role="agent"><AgentProfile /></ProtectedRoute>} />
                        <Route path="/agent/:slug" element={<Layout><AgentPublicPage /></Layout>} />
                        <Route path="/demo" element={<DemoForFMOs />} />
                        <Route path="/pitch" element={<Pitch />} />
                        <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                </BrowserRouter>
            </BrandingProvider>
        </div>
    );
}

export default App;
